import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { DataGrid, DataGridUtils } from '@fashionnovateam/nova-ui-toolkit';
import { useRequestedItemsContext } from '../hooks/useRequestedItems';
import { ColumnGroupHeader } from '../ColumnGroupHeader';
import {
  requiredColumns,
  checkboxColumn,
  baselineCostColumns,
  optionalColumns,
  actionColumn,
  completedExtraColumns,
  requestedDateColumn,
} from '../columns';
import { ItemLeadTime } from '../types';
import { FeatureFlagsContext } from '../../../Lib/Contexts/FeatureFlagsContext';
import { useSubmitItemLeadTime } from '../../../Lib/Hooks/item-lead-time.hooks';
import CountryList from '../../../Lib/Constants/countries';
import { normalizeSubmissionData } from '../utils/normalizeSubmissionData';
import { ItemRequestDataGridProps } from './types';
import { ROW_HEIGHT, PINNED_COLUMN_FIELDS } from './constants';
import { useScrollSync, useDataGridColumns, useTransformedRows } from './hooks';

function ItemRequestDataGrid({
  data,
  isLoading,
  userId,
  vendorId,
  isCompleted = false,
  onRowClick,
  onMessageClick,
  canUpdate = false,
  canSubmit = false,
  editedItems,
  onUpdateItem,
  errors,
  onError,
  totalCount,
  page = 1,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  sort = 'desc',
  onSortChange,
  showBulkActions = false,
  selectedIds,
  onToggleRow,
  onToggleAll,
  isAllSelected = false,
  isPartiallySelected = false,
  ...others
}: ItemRequestDataGridProps) {
  const { flags } = useContext(FeatureFlagsContext);
  const apiRef = DataGridUtils.useGridApiRef();
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const { selectedItem, domesticEligible } = useRequestedItemsContext();
  const { mutate: submitItem, isLoading: isSubmitting } =
    useSubmitItemLeadTime();

  const countrySelectItems = useMemo(
    () => [
      { value: '', label: 'Select..' },
      ...CountryList.map((country) => ({
        value: country.text,
        label: country.text,
        disabled: !domesticEligible && country.value === 'US',
      })),
    ],
    [domesticEligible],
  );

  const isBaselineCostEnabled = flags?.baseline_cost?.enabled ?? false;

  const columns = useDataGridColumns({
    isBaselineCostEnabled,
    isCompleted,
    showBulkActions,
    isAllSelected,
    isPartiallySelected,
    onToggleAll,
  });

  const handleSubmitItem = useCallback(
    (item: ItemLeadTime) => {
      const editedItem = editedItems.find(
        (e) => e.worksheet_id === item.worksheet_id,
      );
      const mergedItem = { ...item, ...editedItem };

      submitItem({
        userId,
        worksheetId: item.worksheet_id,
        data: normalizeSubmissionData({
          cost_jr: Number(mergedItem.cost_jr) || 0,
          cost_plus: Number(mergedItem.cost_plus) || 0,
          country_of_origin: mergedItem.country_of_origin || '',
          shipment_mode: mergedItem.shipment_mode || '',
          exclusivity: mergedItem.exclusivity || 'Not Exclusive',
          earliest_custom_cut_delivery_date:
            mergedItem.earliest_custom_cut_delivery_date || '',
          custom_cut_lead_time_in_weeks:
            Number(mergedItem.custom_cut_lead_time_in_weeks) || 0,
          custom_MOQ_jr: Number(mergedItem.custom_MOQ_jr) || 0,
          custom_MOQ_plus: Number(mergedItem.custom_MOQ_plus) || 0,
          stock_quantity: Number(mergedItem.stock_quantity) || 0,
          stock_ratio: mergedItem.stock_ratio || '',
          earliest_IHD_date_to_deliver_stock:
            mergedItem.earliest_IHD_date_to_deliver_stock || '',
          incoming_stock_quantity:
            Number(mergedItem.incoming_stock_quantity) || 0,
          incoming_stock_ratio: mergedItem.incoming_stock_ratio || '',
          incoming_stock_date: mergedItem.incoming_stock_date || '',
          comments: mergedItem.comments || '',
        }),
      });
    },
    [editedItems, submitItem, userId],
  );

  const transformedRows = useTransformedRows({
    data,
    editedItems,
    errors,
    isCompleted,
    canUpdate,
    canSubmit,
    isBaselineCostEnabled,
    vendorId,
    onRowClick,
    onMessageClick,
    onUpdateItem,
    onError,
    onSubmitItem: handleSubmitItem,
    isSubmitting,
    countrySelectItems,
    selectedIds,
    onToggleRow,
  });

  useEffect(() => {
    if (selectedItem && apiRef?.current?.setRowSelectionModel) {
      apiRef.current.setRowSelectionModel([selectedItem.worksheet_id]);
    }
  }, [selectedItem, apiRef]);

  useScrollSync(apiRef, headerScrollRef);

  const sortModel = useMemo(() => [{ field: 'requested_date', sort }], [sort]);

  const handleSortModelChange = useCallback(
    (model: DataGridUtils.GridSortModel) => {
      if (!onSortChange) return;
      const sortItem = model[0];
      if (sortItem?.field === 'requested_date') {
        onSortChange(sortItem.sort === 'desc' ? 'desc' : 'asc');
      } else {
        // Empty model means user clicked to "unsort" — toggle direction instead
        onSortChange(sort === 'desc' ? 'asc' : 'desc');
      }
    },
    [onSortChange, sort],
  );

  const pinnedColumnFields = useMemo(() => {
    if (showBulkActions && !isCompleted) {
      return ['checkbox', ...PINNED_COLUMN_FIELDS] as string[];
    }
    return [...PINNED_COLUMN_FIELDS] as string[];
  }, [showBulkActions, isCompleted]);

  const { pinnedWidth, requiredWidth, optionalWidth } = useMemo(() => {
    let pinned = requiredColumns
      .filter((col) =>
        PINNED_COLUMN_FIELDS.includes(
          col.field as (typeof PINNED_COLUMN_FIELDS)[number],
        ),
      )
      .reduce((sum, col) => sum + (col.width || 0), 0);

    if (showBulkActions && !isCompleted) {
      pinned += checkboxColumn.width || 0;
    }

    let reqWidth = requiredColumns
      .filter(
        (col) =>
          !PINNED_COLUMN_FIELDS.includes(
            col.field as (typeof PINNED_COLUMN_FIELDS)[number],
          ),
      )
      .reduce((sum, col) => sum + (col.width || 0), 0);

    if (isBaselineCostEnabled) {
      reqWidth += baselineCostColumns.reduce(
        (sum, col) => sum + (col.width || 0),
        0,
      );
    }

    if (isCompleted) {
      reqWidth += completedExtraColumns.reduce(
        (sum, col) => sum + (col.width || 0),
        0,
      );
    } else {
      reqWidth += requestedDateColumn.width || 0;
    }

    let optWidth = optionalColumns.reduce(
      (sum, col) => sum + (col.width || 0),
      0,
    );

    if (!isCompleted) {
      optWidth += actionColumn.width || 0;
    }

    return {
      pinnedWidth: pinned,
      requiredWidth: reqWidth,
      optionalWidth: optWidth,
    };
  }, [isBaselineCostEnabled, isCompleted, showBulkActions]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="sticky top-0">
        <ColumnGroupHeader
          ref={headerScrollRef}
          pinnedWidth={pinnedWidth}
          requiredWidth={requiredWidth}
          optionalWidth={optionalWidth}
        />
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        <DataGrid
          key={`datagrid-page-${page}`}
          apiRef={apiRef}
          hideFilterButton
          hideGroupButton
          rows={transformedRows ?? []}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          loading={isLoading}
          rowHeight={ROW_HEIGHT}
          rowCount={totalCount ?? data?.length ?? 0}
          paginationModel={{
            page: page - 1,
            pageSize,
          }}
          onPaginationModelChange={(model) => {
            const pageSizeChanged = model.pageSize !== pageSize;
            const pageChanged = model.page !== page - 1;

            if (pageSizeChanged && onPageSizeChange) {
              onPageSizeChange(model.pageSize);
              // Page size change handler resets page to 1, so don't also call onPageChange
              return;
            }

            if (pageChanged && onPageChange) {
              onPageChange(model.page + 1);
            }
          }}
          pageSizeOptions={pageSizeOptions}
          initialState={{
            pinnedColumns: {
              left: pinnedColumnFields,
            },
          }}
          columns={columns}
          classes={{
            root: 'rounded-t-none border-t-0',
          }}
          {...others}
        />
      </div>
    </div>
  );
}

export default ItemRequestDataGrid;
